import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { Select as AntSelect, Drawer, Input, SelectProps } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { CheckOutlined, SearchOutlined } from '@ant-design/icons';

import { ArrowIcon, XIcon } from '~shared/ui/Icons/icons';
import { useTranslation } from '~shared/lib/i18n';
import { PlaceholderAsLabel } from '~shared/ui/layout';
import { useIsMobile } from '~shared/ui/utils/useIsMobile';

const HEIGHT_BY_SIZE: Record<NonNullable<SelectProps['size']>, string> = {
  large: 'h-[46px] text-[15px] px-3.5',
  middle: 'h-[34px] text-[14px] px-3',
  small: 'h-[28px] text-[13px] px-2.5',
};

const SCROLL_STYLES = `
  .balalyk-select-trigger {
    -webkit-tap-highlight-color: transparent;
    border: 1px solid var(--stroke) !important;
  }
  .balalyk-select-list { overscroll-behavior: contain; }
`;

const isFlatOption = (
  o: DefaultOptionType
): o is DefaultOptionType & { value: string | number; label?: React.ReactNode } =>
  Boolean(o) && (typeof o.value === 'string' || typeof o.value === 'number');

const optionText = (o: DefaultOptionType): string => {
  if (typeof o.label === 'string' || typeof o.label === 'number') {
    return String(o.label);
  }

  return String(o.value ?? '');
};

export const Select: FC<SelectProps> = ({
  size = 'large',
  suffixIcon = <ArrowIcon />,
  clearIcon = <XIcon />,
  placeholder,
  value,
  options,
  mode,
  showSearch,
  disabled,
  allowClear,
  onChange,
  onSelect,
  labelInValue,
  optionRender,
  ...props
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const flatOptions = useMemo<DefaultOptionType[]>(
    () => (Array.isArray(options) ? options.filter(isFlatOption) : []),
    [options]
  );

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [internalValue, setInternalValue] = useState<SelectProps['value']>(undefined);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!drawerOpen) {
      return;
    }

    const id = window.requestAnimationFrame(() => {
      const list = listRef.current;

      if (!list) {
        return;
      }

      const selected = list.querySelector<HTMLElement>('[data-selected="true"]');

      if (!selected) {
        return;
      }

      const offset = selected.offsetTop - list.clientHeight / 2 + selected.clientHeight / 2;
      list.scrollTop = Math.max(0, offset);
    });

    return () => window.cancelAnimationFrame(id);
  }, [drawerOpen, search]);

  const effectiveValue = value === undefined ? internalValue : value;

  const canUseDrawer = isMobile && !mode && flatOptions.length === (options?.length ?? 0);

  const filtered = useMemo(() => {
    if (!showSearch || !search.trim()) {
      return flatOptions;
    }

    const q = search.trim().toLowerCase();

    return flatOptions.filter((o) => optionText(o).toLowerCase().includes(q));
  }, [flatOptions, search, showSearch]);

  const resolvedPlaceholder = placeholder === undefined ? t('actions.select') : placeholder;

  if (canUseDrawer) {
    const openDrawer = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) {
        return;
      }

      event.currentTarget.blur();
      setSearch('');
      setDrawerOpen(true);
    };

    const currentValue =
      labelInValue && effectiveValue && typeof effectiveValue === 'object'
        ? (effectiveValue as { value: string | number }).value
        : (effectiveValue as string | number | undefined);

    const hasValue = currentValue !== undefined && currentValue !== null && currentValue !== '';

    const displayLabel = (() => {
      if (!hasValue) {
        return null;
      }

      if (labelInValue && effectiveValue && typeof effectiveValue === 'object') {
        const lv = effectiveValue as { label?: React.ReactNode; value: string | number };

        if (typeof lv.label === 'string' || typeof lv.label === 'number') {
          return String(lv.label);
        }
      }

      const matched = flatOptions.find((o) => String(o.value) === String(currentValue));

      return matched ? optionText(matched) : String(currentValue);
    })();

    const handlePick = (option: DefaultOptionType) => {
      const picked = labelInValue
        ? { ...option, value: option.value, label: option.label, key: String(option.value) }
        : option.value;

      setInternalValue(picked);
      onChange?.(picked as never, option as never);
      onSelect?.(picked as never, option as never);
      setDrawerOpen(false);
    };

    const handleClear = (event: React.MouseEvent) => {
      event.stopPropagation();
      setInternalValue(undefined);
      onChange?.(undefined as never, [] as never);
    };

    return (
      <>
        <style>{SCROLL_STYLES}</style>
        <PlaceholderAsLabel placeholder={resolvedPlaceholder} value={effectiveValue} size={size}>
          <button
            type="button"
            disabled={disabled}
            onClick={openDrawer}
            className={`balalyk-select-trigger w-full flex items-center appearance-none rounded-[10px] bg-content text-black outline-none text-left ${
              HEIGHT_BY_SIZE[size]
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span
              className={`flex-1 min-w-0 truncate ${hasValue ? 'text-black' : 'text-placeholder'}`}
            >
              {hasValue ? displayLabel : String(resolvedPlaceholder ?? '')}
            </span>
            <span className="flex items-center gap-1.5 shrink-0 ml-2">
              {allowClear !== false && hasValue && !disabled ? (
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={handleClear}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleClear(e as unknown as React.MouseEvent);
                    }
                  }}
                  className="flex items-center justify-center w-5 h-5 rounded-full text-placeholder cursor-pointer"
                >
                  {clearIcon as React.ReactNode}
                </span>
              ) : null}
              <span
                className={`flex items-center text-placeholder ${drawerOpen ? 'rotate-180' : ''}`}
              >
                {suffixIcon as React.ReactNode}
              </span>
            </span>
          </button>
        </PlaceholderAsLabel>

        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          placement="bottom"
          height="auto"
          zIndex={1500}
          closable={false}
          getContainer={() => document.body}
          styles={{
            wrapper: { maxHeight: '85vh' },
            body: {
              padding: 0,
              background: 'var(--content)',
            },
            content: {
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              background: 'var(--content)',
              overflow: 'hidden',
            },
            header: { display: 'none' },
            mask: { background: 'rgba(2, 6, 24, 0.5)' },
          }}
        >
          <div className="flex flex-col px-4">
            <div className="pt-2.5 pb-1 flex justify-center">
              <span className="block w-9 h-1 rounded-full bg-stroke" />
            </div>

            <div className="flex items-center justify-between gap-3 pt-2 pb-3">
              <div
                className="min-w-0 flex-1 truncate text-[17px] font-semibold text-black"
                title={typeof resolvedPlaceholder === 'string' ? resolvedPlaceholder : undefined}
              >
                {resolvedPlaceholder}
              </div>
              <div onClick={() => setDrawerOpen(false)}>
                <XIcon />
              </div>
            </div>

            {showSearch && flatOptions.length > 0 ? (
              <div className="pb-3">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  prefix={<SearchOutlined className="text-placeholder" />}
                  placeholder={t('kids:search') || ''}
                  allowClear
                  size="large"
                  className="rounded-[12px]"
                />
              </div>
            ) : null}

            <div className="h-px bg-stroke/70 mx-5" />
          </div>

          <div
            ref={listRef}
            className="balalyk-select-list overflow-y-auto px-4 pt-2 pb-[max(16px,env(safe-area-inset-bottom))]"
            style={{
              maxHeight:
                showSearch && flatOptions.length > 0 ? 'calc(85vh - 170px)' : 'calc(85vh - 110px)',
            }}
          >
            {filtered.length === 0 ? (
              <div className="px-4 py-12 text-center text-placeholder text-[14px]">
                {t('notify.noData')}
              </div>
            ) : (
              <div className="flex flex-col gap-0.5 -mx-4 px-4">
                {filtered.map((o, index) => {
                  const selected =
                    currentValue !== undefined && String(o.value) === String(currentValue);

                  const fallbackLabel: React.ReactNode =
                    o.label !== undefined && o.label !== null ? o.label : optionText(o);

                  const labelNode: React.ReactNode = optionRender
                    ? optionRender(
                        {
                          ...o,
                          label: o.label,
                          value: o.value as string | number,
                          key: String(o.value),
                          data: o,
                        },
                        { index }
                      )
                    : fallbackLabel;

                  return (
                    <button
                      type="button"
                      key={String(o.value)}
                      data-selected={selected ? 'true' : undefined}
                      disabled={Boolean(o.disabled)}
                      onClick={() => handlePick(o)}
                      className={`w-full flex items-start justify-between gap-3 px-3 py-2.5 text-left rounded-[10px] min-h-[44px] border-0 bg-transparent outline-none disabled:opacity-50 ${
                        selected
                          ? 'bg-primary/10 text-primary font-semibold'
                          : 'text-black active:bg-stroke/60'
                      }`}
                    >
                      <div className="flex-1 min-w-0 whitespace-normal break-words text-[15px]">
                        {labelNode}
                      </div>
                      {selected ? (
                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white shrink-0 mt-0.5">
                          <CheckOutlined className="text-[11px]" />
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </Drawer>
      </>
    );
  }

  return (
    <PlaceholderAsLabel placeholder={resolvedPlaceholder} value={value} size={size}>
      <AntSelect
        size={size}
        suffixIcon={suffixIcon}
        clearIcon={clearIcon}
        allowClear={allowClear ?? true}
        options={options}
        mode={mode}
        showSearch={showSearch}
        disabled={disabled}
        labelInValue={labelInValue}
        onChange={onChange}
        onSelect={onSelect}
        optionRender={optionRender}
        {...props}
        value={value}
        placeholder={placeholder || t('actions.select')}
      />
    </PlaceholderAsLabel>
  );
};
